import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import type { IDEAL_GAS_LAW_CALCULATOR_STRINGS } from "@/components/tools/ideal-gas-law-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { GAS_CONSTANT } from "@/lib/tool-kit/chem-physical/constants";
import {
  GAS_SI_UNITS,
  GAS_TERMS,
  type GasSolution,
  type GasTerm,
  gasUnitSymbol,
  HIGH_PRESSURE_PA,
} from "@/lib/tool-kit/chem-physical/gas";
import { formatPlain, formatSig } from "@/lib/tool-kit/chemistry/format";

type Strings = typeof IDEAL_GAS_LAW_CALCULATOR_STRINGS;

const REARRANGED: Record<GasTerm, keyof Strings> = {
  p: "rearrangedP",
  v: "rearrangedV",
  n: "rearrangedN",
  t: "rearrangedT",
};

const SUBSTITUTED: Record<GasTerm, keyof Strings> = {
  p: "substitutedP",
  v: "substitutedV",
  n: "substitutedN",
  t: "substitutedT",
};

const R_DIGITS = 10;

export function GasAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: GasSolution;
  /** What the reader typed, echoed in the unit-conversion lines. */
  texts: Record<GasTerm, string>;
  units: Record<GasTerm, string>;
  sig: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const { unknown, si } = solution;
  const labels: Record<GasTerm, string> = {
    p: strings.answerP,
    v: strings.answerV,
    n: strings.answerN,
    t: strings.answerT,
  };
  const answerText = `${formatSig(fmt, solution.answer, sig)} ${gasUnitSymbol(unknown, solution.answerUnit)}`;
  const r = fmt(GAS_CONSTANT, { maximumFractionDigits: R_DIGITS - 1 });
  const conversions = GAS_TERMS.filter(
    (term) => term !== unknown && units[term] !== GAS_SI_UNITS[term].id
  ).map((term) =>
    t(strings.convertLine, {
      label: labels[term],
      value: texts[term].trim(),
      unit: gasUnitSymbol(term, units[term]),
      converted: formatPlain(fmt, si[term]),
      target: GAS_SI_UNITS[term].symbol,
    })
  );
  const substituted = t(strings[SUBSTITUTED[unknown]], {
    p: formatPlain(fmt, si.p),
    v: formatPlain(fmt, si.v),
    n: formatPlain(fmt, si.n),
    t: formatPlain(fmt, si.t),
    r,
    result: formatSig(fmt, si[unknown], sig + 1),
  });
  const needsConversion = units[unknown] !== GAS_SI_UNITS[unknown].id;
  const vmSig = Math.max(sig, 3);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard
          label={strings.molarVolume}
          value={`${formatSig(fmt, solution.molarVolume, vmSig)} L/mol`}
        />
        <StatCard label={strings.gasConstant} value={`${r} J/(mol·K)`} />
      </StatGrid>
      {si.p > HIGH_PRESSURE_PA ? <Notice>{strings.highPressure}</Notice> : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.equation}</p>
        <p>{strings[REARRANGED[unknown]]}</p>
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        {needsConversion ? (
          <p>
            {t(strings.answerLine, {
              result: formatSig(fmt, si[unknown], sig + 1),
              siUnit: GAS_SI_UNITS[unknown].symbol,
              answer: answerText,
            })}
          </p>
        ) : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}
